import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import {
  EvaluationTest,
  EvaluationProviderConfig as EvaluationProviderConfigType,
  PromptMode,
} from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { EvaluationProviderConfig as EvaluationProviderConfigComponent } from './EvaluationProviderConfig';
import { AIRetestConflictDialog } from './AIRetestConflictDialog';

/**
 * Generate AI retest name from source test name.
 * Strips existing " - AI Test YYYY-MM-DD HH:mm" suffix before appending new one.
 */
function generateRetestName(sourceName: string): string {
  const stripped = sourceName.replace(/ - AI Test \d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, '');
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return `${stripped} - AI Test ${dateStr}`;
}

export const AIRetestPage = () => {
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const evaluationApi = EvaluationApiService.getInstance();

  const [sourceTest, setSourceTest] = useState<EvaluationTest | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [retestRunningWarning, setRetestRunningWarning] = useState<string | null>(null);
  const [executionCount, setExecutionCount] = useState(0);

  // Form state
  const [testName, setTestName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [promptMode, setPromptMode] = useState<PromptMode>('Fixed');
  const [fixedPrompt, setFixedPrompt] = useState('');
  const [providerConfigs, setProviderConfigs] = useState<EvaluationProviderConfigType[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [customPrompts, setCustomPrompts] = useState<string[] | null>(null);
  const [promptFileError, setPromptFileError] = useState<string | null>(null);
  const [promptFileName, setPromptFileName] = useState<string | null>(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  useEffect(() => {
    if (testId) {
      loadSourceTest();
    }
  }, [testId]);

  const loadSourceTest = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [test, retestStatus] = await Promise.all([
        evaluationApi.getEvaluationTest(testId!),
        evaluationApi.isRetestRunning(),
      ]);

      if (!test) {
        setError('Source test not found');
        return;
      }

      setSourceTest(test);
      setTestName(generateRetestName(test.name));
      setPurpose(test.purpose);
      setPromptMode(test.mode);
      setFixedPrompt(test.fixedPrompt || '');
      setProviderConfigs(test.providerConfigs.map(c => ({ ...c, id: crypto.randomUUID() })));

      if (retestStatus.isRunning) {
        setRetestRunningWarning('Another AI evaluation is currently running. You can still configure this retest, but submitting may fail.');
      }

      // Load execution count for Open mode warning
      if (test.mode === 'Open') {
        try {
          const executions = await evaluationApi.listExecutions(testId!);
          setExecutionCount(executions.length);
        } catch {
          // Non-critical, continue
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load source test');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!testName.trim()) {
      errors.testName = 'Test name is required';
    }

    providerConfigs.forEach((config, index) => {
      if (!config.name.trim()) {
        errors[`provider_${index}_name`] = `Provider ${index + 1} name is required`;
      }
    });

    if (promptMode === 'Fixed' && !fixedPrompt.trim()) {
      errors.fixedPrompt = 'Fixed prompt is required in Fixed mode';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePromptFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPromptFileError(null);
    setPromptFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (!Array.isArray(parsed) || !parsed.every((item) => typeof item === 'string')) {
          throw new Error('JSON must be an array of strings');
        }
        setCustomPrompts(parsed);
      } catch {
        setPromptFileError('Invalid JSON format. Expected an array of strings.');
        setCustomPrompts(null);
        setPromptFileName(null);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleClearPrompts = () => {
    setCustomPrompts(null);
    setPromptFileError(null);
    setPromptFileName(null);
    setShowPromptPreview(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !sourceTest) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const modifiedConfig: EvaluationTest = {
        ...sourceTest,
        name: testName.trim(),
        purpose: purpose.trim(),
        mode: promptMode,
        fixedPrompt: promptMode === 'Fixed' ? fixedPrompt.trim() : undefined,
        providerConfigs,
      };

      const result = await evaluationApi.startAIRetest(testId!, modifiedConfig, customPrompts ?? undefined);

      alert(`AI re-evaluation started with ${result.promptCount} prompts. Results will appear as they complete.`);
      navigate(`/opinions/evaluation/${result.testId}/analytics`);
    } catch (err: any) {
      if (err.message?.includes('Another AI evaluation is currently running')) {
        setShowConflictDialog(true);
      } else {
        setError(err.message || 'Failed to start AI retest');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate('/opinions/evaluation');
  };

  if (!testId) {
    navigate('/opinions/evaluation');
    return null;
  }

  return (
    <div data-component="AIRetestPage" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">AI Evaluation Retest</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading && (
            <div data-flow="loading" className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading source test...</p>
            </div>
          )}

          {error && (
            <div data-flow="error" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {retestRunningWarning && (
            <div data-flow="retest-warning" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-800 text-sm">{retestRunningWarning}</p>
            </div>
          )}

          {sourceTest && sourceTest.mode === 'Open' && executionCount > 50 && !customPrompts && (
            <div data-flow="execution-limit-warning" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-yellow-800 text-sm">
                Only the last 50 executions will be re-evaluated (out of {executionCount}).
              </p>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div data-flow="validation-errors" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 font-medium mb-2">Please fix the following errors:</p>
              <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1">
                {Object.values(validationErrors).map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {!isLoading && sourceTest && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Source Test Info */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Source test:</span> {sourceTest.name}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {customPrompts
                    ? `${customPrompts.length} custom prompt${customPrompts.length !== 1 ? 's' : ''} will be executed`
                    : sourceTest.mode === 'Open'
                      ? `${executionCount} execution${executionCount !== 1 ? 's' : ''} will be re-evaluated`
                      : 'Fixed prompt will be re-evaluated with modified configuration'}
                </p>
              </div>

              {/* Custom Prompts */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Custom Prompts</h3>
                <div className="space-y-3">
                  <div>
                    <label htmlFor="promptFile" className="block text-sm font-medium text-gray-700 mb-1">
                      Upload a JSON file with an array of prompt strings
                    </label>
                    <input
                      id="promptFile"
                      type="file"
                      accept=".json"
                      onChange={handlePromptFileUpload}
                      disabled={isSubmitting}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
                    />
                  </div>

                  {promptFileError && (
                    <p className="text-sm text-red-600">{promptFileError}</p>
                  )}

                  {customPrompts && !promptFileError && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <p className="text-sm text-green-800">
                          {customPrompts.length} prompts loaded from {promptFileName}
                        </p>
                        <button
                          type="button"
                          onClick={handleClearPrompts}
                          disabled={isSubmitting}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Clear
                        </button>
                      </div>

                      {customPrompts.length > 0 && (
                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={() => setShowPromptPreview(!showPromptPreview)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {showPromptPreview ? 'Hide preview' : 'Show preview'}
                          </button>

                          {showPromptPreview && (
                            <div className="mt-2 bg-white border border-green-200 rounded p-2 max-h-40 overflow-y-auto">
                              {customPrompts.slice(0, 5).map((prompt, index) => (
                                <p key={index} className="text-xs text-gray-600 border-b border-gray-100 last:border-0 pb-1 mb-1 last:pb-0 last:mb-0">
                                  {prompt.length > 100 ? `${prompt.substring(0, 100)}...` : prompt}
                                </p>
                              ))}
                              {customPrompts.length > 5 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  and {customPrompts.length - 5} more...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="testName" className="block text-sm font-medium text-gray-700 mb-1">
                      Test Name *
                    </label>
                    <input
                      id="testName"
                      type="text"
                      value={testName}
                      onChange={(e) => setTestName(e.target.value)}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                        validationErrors.testName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.testName && (
                      <p className="text-sm text-red-600 mt-1">{validationErrors.testName}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-1">
                      Purpose
                    </label>
                    <textarea
                      id="purpose"
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      disabled={isSubmitting}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Prompt Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Prompt Configuration</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prompt Mode
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Fixed"
                          checked={promptMode === 'Fixed'}
                          onChange={(e) => setPromptMode(e.target.value as PromptMode)}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Fixed</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          value="Open"
                          checked={promptMode === 'Open'}
                          onChange={(e) => setPromptMode(e.target.value as PromptMode)}
                          disabled={isSubmitting}
                          className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Open</span>
                      </label>
                    </div>
                  </div>

                  {promptMode === 'Fixed' && (
                    <div data-flow="fixed-mode">
                      <label htmlFor="fixedPrompt" className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Prompt *
                      </label>
                      <textarea
                        id="fixedPrompt"
                        value={fixedPrompt}
                        onChange={(e) => setFixedPrompt(e.target.value)}
                        disabled={isSubmitting}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
                          validationErrors.fixedPrompt ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {validationErrors.fixedPrompt && (
                        <p className="text-sm text-red-600 mt-1">{validationErrors.fixedPrompt}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Provider Configurations */}
              <EvaluationProviderConfigComponent
                configs={providerConfigs}
                onConfigsChange={setProviderConfigs}
                disabled={isSubmitting}
                validationErrors={validationErrors}
                mode="multi"
              />

              {/* Footer */}
              <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting Retest...
                    </>
                  ) : (
                    'Start AI Retest'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Conflict Dialog */}
      {showConflictDialog && (
        <AIRetestConflictDialog onClose={() => setShowConflictDialog(false)} />
      )}
    </div>
  );
};
